// signalStore.js — shared, in-memory sink for signal (trigger) results.
//
// Signals are "triggers": render-less kernels evaluated client-side per
// hydrated prompt (see lib/triggers/triggerHarness.js). Whichever surface is on
// screen owns the DRIVER (it decides which prompts to run over) and writes its
// verdicts here; any other surface reads the same annotations. Today: the
// trace-log overlay, the signals console, and the per-signal/skill detail lens.
//
// Two read shapes:
//   - resultsByTraceId: Map<traceId, fired[]>  — per-prompt verdicts (chips, lists)
//   - seriesByCommand:  Map<command, number[]> — per-signal firing-rate series
//     over the current scan window, PUBLISHED by the driver on completion (so
//     the sidebar facet sparklines update ~twice per scan, not per result).
//
// Consume via useSyncExternalStore(subscribeSignals, getSignalResults | getSignalRun
// | getSignalSeries); snapshots are stable refs between updates (Object.is).

import { onAuthIdentityChange } from "./authTokenStore.js";

export const BUCKETS = 32;

function emptyState() {
  return {
  resultsByTraceId: new Map(), // traceId -> fired[]  ({ command, type, confidence, value })
  run: { done: 0, total: 0, running: false },
  seriesByCommand: new Map(),  // command -> number[]  (firing-rate over the scan window)
  scanningCommands: new Set(),  // commands with an in-flight scan — drives the
                                // sidebar facet's per-signal loading treatment
                                // (set by whichever driver owns the scan).
  scannedIds: null,            // Set of trace ids the current results cover. Lets
                               // another surface (the per-signal detail lens) tell
                               // its window is COVERED by a completed scan and
                               // REUSE these results instead of re-scanning. A Set
                               // (not a joined key) so reuse is order-independent
                               // and tolerates a superset window.
  };
}
let state = emptyState();
const subscribers = new Set();
function notify() { subscribers.forEach((fn) => fn()); }

export function getSignalResults() { return state.resultsByTraceId; }
export function getSignalRun() { return state.run; }
export function getSignalSeries() { return state.seriesByCommand; }
export function getSignalScanning() { return state.scanningCommands; }
export function getSignalScannedIds() { return state.scannedIds; }

// Persistent cache of the most recent COMPLETED full scan (the console scanning
// every cheap signal over a window). Lives OUTSIDE the volatile run state on
// purpose: surfaces reset the run state on mount/unmount for live badging (e.g.
// TraceLogPanel clears it on unmount), which would otherwise wipe a just-
// completed scan before the per-signal detail lens can reuse it. This cache
// survives that churn so the detail can seed from it instead of re-scanning the
// same window. Only a full-scan driver (the console) publishes here.
let scanCache = null; // { ids: Set<traceId>, results: Map<traceId, fired[]>, signature }
// `signature` identifies the SIGNAL SET this scan covered (command + content
// version of every cheap signal). Reuse is gated on it so authoring or editing
// a signal invalidates the cache — otherwise a new signal would read its
// empty cached slot ("0 fired", never scanned) and an edited one would show
// pre-edit verdicts, both over an already-scanned window.
export function publishScanCache(ids, results, signature = null) {
  scanCache = { ids: new Set(ids), results: new Map(results), signature };
}
export function getScanCache() { return scanCache; }

// Verdicts and completed scans are tenant-derived even when trace IDs collide.
// Clear them synchronously before mounted subscribers render a new auth epoch.
onAuthIdentityChange(() => {
  state = emptyState();
  scanCache = null;
  notify();
});

// Signature of a signal set: command + content version (updatedAt) of each,
// order-independent. Changes when a signal is added, removed, or edited, so it
// can key the scan cache and the scan effect's deps — an authored/edited signal
// then yields a fresh scan rather than stale reuse.
export function signalSetSignature(signals) {
  if (!Array.isArray(signals)) return "";
  return signals.map((t) => `${t.command}:${t.updatedAt || 0}`).sort().join("|");
}

// Can a cached scan be reused for the current window + signal set? Reuse ONLY
// when the same signal set was scanned (signature match) AND the cache covers
// every prompt in the current window (set-coverage — tolerates row order / a
// superset cached window). A mismatch on either falls through to a fresh scan.
export function scanCacheCovers(cache, ids, signature) {
  if (!cache || !cache.ids || !cache.ids.size) return false;
  if (cache.signature !== signature) return false;
  return ids.every((id) => cache.ids.has(id));
}

// Seed a COMPLETED scan's results into the volatile store WITHOUT re-scanning —
// the detail lens reusing the cached console scan. Marks the run settled so the
// sidebar reads it as evaluated and signal-scoped facets see the fired set.
export function seedSignalResults(scannedIds, results) {
  const n = scannedIds ? scannedIds.length : results.size;
  state = {
    ...state,
    resultsByTraceId: new Map(results),
    run: { done: n, total: n, running: false },
    scannedIds: scannedIds ? new Set(scannedIds) : null,
  };
  notify();
}

// Begin (or clear) a run over `total` rows. total === 0 clears to idle. Clears
// the published series too, so the sidebar sparklines flatten then refill.
// `scannedIds` is the trace-id list the scan covers (stored as a Set) so a later
// surface whose window is covered can reuse the results.
export function resetSignals(total = 0, scannedIds = null) {
  state = {
    ...state,
    resultsByTraceId: new Map(),
    run: { done: 0, total, running: total > 0 },
    seriesByCommand: new Map(),
    scannedIds: scannedIds ? new Set(scannedIds) : null,
  };
  notify();
}

// Record one row's verdict and advance progress. Preserves seriesByCommand
// (so its ref is stable across the per-result churn — sidebar sparklines don't
// re-render until the driver republishes the series).
export function recordSignalResult(traceId, fired, run) {
  const results = fired && fired.length
    ? new Map(state.resultsByTraceId).set(traceId, fired)
    : state.resultsByTraceId;
  state = { ...state, resultsByTraceId: results, run };
  notify();
}

export function endSignalRun() {
  if (!state.run.running) return;
  state = { ...state, run: { ...state.run, running: false } };
  notify();
}

// Publish the full per-command firing series (the signals console, scanning all
// cheap signals).
export function setSignalSeries(map) {
  state = { ...state, seriesByCommand: map instanceof Map ? map : new Map() };
  notify();
}

// Publish one signal's series, keeping the others (a per-signal detail lens).
export function mergeSignalSeries(command, series) {
  const next = new Map(state.seriesByCommand);
  next.set(command, series);
  state = { ...state, seriesByCommand: next };
  notify();
}

// Mark a set of commands as scanning (replace the whole set — the console
// driver, which scans every cheap signal at once). Empty clears.
export function setSignalScanning(commands) {
  state = { ...state, scanningCommands: commands instanceof Set ? commands : new Set(commands || []) };
  notify();
}

// Toggle one command's scanning flag, keeping the rest (a per-signal detail
// driver scanning just its own signal).
export function markSignalScanning(command, on) {
  if (!command) return;
  const has = state.scanningCommands.has(command);
  if (has === Boolean(on)) return;
  const next = new Set(state.scanningCommands);
  if (on) next.add(command); else next.delete(command);
  state = { ...state, scanningCommands: next };
  notify();
}

export function subscribeSignals(callback) {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

function traceTimeMs(t) {
  const v = t?.timestamp;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

// Bucket fired verdicts into a per-command firing-rate series over the time span
// of `windowTraces` (the scanned window). Returns Map<command, number[]>.
export function bucketFiringByCommand(windowTraces, resultsMap, buckets = BUCKETS) {
  const byCmd = new Map();
  const arr = Array.isArray(windowTraces) ? windowTraces : [];
  const times = [];
  for (const t of arr) { const ms = traceTimeMs(t); if (ms != null) times.push(ms); }
  if (times.length < 2 || !resultsMap || resultsMap.size === 0) return byCmd;
  const min = Math.min(...times);
  const span = Math.max(1, Math.max(...times) - min);
  const byId = new Map(arr.map((t) => [t.id, t]));
  for (const [id, fired] of resultsMap.entries()) {
    const t = byId.get(id);
    const ms = t ? traceTimeMs(t) : null;
    if (ms == null) continue;
    const b = Math.min(buckets - 1, Math.max(0, Math.floor(((ms - min) / span) * buckets)));
    for (const f of fired) {
      let a = byCmd.get(f.command);
      if (!a) { a = new Array(buckets).fill(0); byCmd.set(f.command, a); }
      a[b] += 1;
    }
  }
  return byCmd;
}
